import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { TradableAreaRegistrationIssue_MissingMaster } from '@ebalance/proto/bffuser/v1/tradable_area_service_pb'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { TertiaryButton } from '@enechain/ecloud-designsystem'
import {
  FaInboxRegular,
  FaMagnifyingGlassRegular,
} from '@enechain/ecloud-icons'

import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'
import { convertAreaLabel } from '~/models/area'
import { routePaths } from '~/routes/routePaths'

/**
 * (b) の説明の先頭に区分名を入れる時、 区分名が英字で終わる (「需要 BG」) なら日本語との境界に
 * 半角スペースを入れる文を使う (`docs/specs/conventions.md`「半角スペースの入れどころ」。 spec の
 * 文言表は `{選択中の発注主体区分}では、`)。
 */
const endsWithLatin = (label: string): boolean => /[A-Za-z]$/.test(label)

const MASTER_NAME_KEY = {
  [TradableAreaRegistrationIssue_MissingMaster.SUBMITTER]:
    'page:biddingSpot.businessAreaEmpty.masterNotRegistered.masterName.submitter',
  [TradableAreaRegistrationIssue_MissingMaster.DEMAND_PROCUREMENT]:
    'page:biddingSpot.businessAreaEmpty.masterNotRegistered.masterName.demandProcurement',
  [TradableAreaRegistrationIssue_MissingMaster.BG]:
    'page:biddingSpot.businessAreaEmpty.masterNotRegistered.masterName.bg',
} as const

type KnownMissingMaster = keyof typeof MASTER_NAME_KEY

const isKnownMissingMaster = (
  master: TradableAreaRegistrationIssue_MissingMaster,
): master is KnownMissingMaster => master in MASTER_NAME_KEY

/**
 * (b) の見出しと説明。 AC-IJM-012 では未登録のエリアの名前が必ず 1 つに決まるので、 spec に
 * {マスタ名} の無い型は無い。 backend が理由を返さない時 (取得失敗・未指定・未知の値) だけの守りとして、
 * 区分から名前を推測せず (原因でないマスタへの登録を案内しないため)、 登録先を「広域機関」とまとめた
 * base からの文にする。
 */
const masterNotRegisteredText = (
  t: TFunction,
  issuerLabel: string | undefined,
  missingMasters: readonly TradableAreaRegistrationIssue_MissingMaster[],
): { title: string; description: string } => {
  const masterNames = missingMasters
    .filter(isKnownMissingMaster)
    .map((master) => t(MASTER_NAME_KEY[master]))
  if (masterNames.length === 0) {
    return {
      title: t('page:biddingSpot.businessAreaEmpty.masterNotRegistered.title'),
      description:
        issuerLabel === undefined
          ? t(
              'page:biddingSpot.businessAreaEmpty.masterNotRegistered.description',
            )
          : endsWithLatin(issuerLabel)
            ? t(
                'page:biddingSpot.businessAreaEmpty.masterNotRegistered.descriptionOfIssuerLatin',
                { issuer: issuerLabel },
              )
            : t(
                'page:biddingSpot.businessAreaEmpty.masterNotRegistered.descriptionOfIssuer',
                { issuer: issuerLabel },
              ),
    }
  }
  const master = masterNames.join(
    t(
      'page:biddingSpot.businessAreaEmpty.masterNotRegistered.masterName.separator',
    ),
  )
  return {
    title: t(
      'page:biddingSpot.businessAreaEmpty.masterNotRegistered.titleOfMaster',
      { master },
    ),
    description:
      issuerLabel === undefined
        ? t(
            'page:biddingSpot.businessAreaEmpty.masterNotRegistered.descriptionOfMaster',
            { master },
          )
        : endsWithLatin(issuerLabel)
          ? t(
              'page:biddingSpot.businessAreaEmpty.masterNotRegistered.descriptionOfIssuerLatinOfMaster',
              { issuer: issuerLabel, master },
            )
          : t(
              'page:biddingSpot.businessAreaEmpty.masterNotRegistered.descriptionOfIssuerOfMaster',
              { issuer: issuerLabel, master },
            ),
  }
}

type Props = {
  /**
   * 選択中の発注主体区分で広域マスタに登録済みの JEPX 対象エリアが 1 つも無いか
   * (AC-BSP-105 の分岐判定)。 true = 分岐 (b)「広域マスタへの登録が必要です」、
   * false = 分岐 (a)「業務エリアに発注できるエリアを追加してください」。
   */
  isBroadAreaMasterEmpty: boolean
  /**
   * 分岐 (b) の説明文の先頭に入れる、 選択中の発注主体区分の名前。 区分で絞らない間
   * (申請セットに絞り込み中など) は渡さず、 区分名の無い文を出す。 分岐 (a) では使われない。
   */
  issuerLabel?: string
  /**
   * 分岐 (b) の {マスタ名} に出す、 登録が足りないマスタ (spec の並び順)。 空の間は登録先を
   * 「広域機関」とまとめた文にする。 分岐 (a) では使われない。
   */
  missingMasters?: readonly TradableAreaRegistrationIssue_MissingMaster[]
  /**
   * 分岐 (a) の説明文に列挙する、 選択中の発注主体区分で発注できる (= 取引可能な) JEPX
   * 取引対象エリア (北→南順)。 `isBroadAreaMasterEmpty` が true の間は使われない。
   */
  orderableAreaCodes: readonly AreaCode[]
  /**
   * 表示中のタブの、 発注主体区分が空欄の取り込んだ札のうち業務エリアにあるものの件数
   * (AC-BSP-105。 数え方は `spotUnknownIssuerBidCount`)。 1 件以上の時だけ説明の後に 1 文を足す。
   */
  unknownIssuerBidCount?: number
}

/**
 * 選択できるエリア (= 業務エリア設定 ∩ JEPX 取引対象 9 エリア ∩ 選択中の発注主体
 * 区分で広域マスタに登録済みのエリア) が 0 件と確定したときに、 エリア依存
 * コンテンツの代わりに出す空状態パネル (AC-BSP-105、 2026-09-21 改訂)。
 *
 * ## 2 分岐 (AC-BSP-105)
 *
 * 原因によって案内を出し分ける:
 * - (a) 広域マスタに登録済みの JEPX 対象エリアが 1 つ以上あるが、 業務エリア設定が
 *   それを含んでいない → 個人設定へ誘導すれば利用者自身で解消できる
 * - (b) 広域マスタに登録済みの JEPX 対象エリアが 1 つも無い → 業務エリア設定を
 *   どう変えても発注できるようにならないため、 管理者への登録依頼を文章のみで
 *   案内する (登録権限を持たない利用者に押せない導線を見せない)
 *
 * 判定順 (AC-BSP-005d → 本 AC) と、 未確定 (取得中 / 取得失敗) を空集合と
 * みなさない扱いは呼び出し元 (`isAreaSelectionEmpty` / `isBroadAreaMasterEmpty`)
 * が持つ。 本 component は確定後の 2 分岐の見た目だけを持つ。
 *
 * ## 見た目・障害通知にしない扱いは共通パネルが持つ
 *
 * 器は `~/components/common/EmptyPanel` (= `design/ui-feasibility/components/
 * empty-panel.md` の canonical 実装)。 赤いエラー表現にも `role="alert"` にも
 * しないのは、 いずれの分岐も有効な設定・マスタ状態だから (spec 注記「障害・
 * 送信失敗を意味する赤いエラー表現にはしない」)。
 *
 * glyph は分岐で描き分ける (empty-panel.md の対応表): 分岐 (a) は
 * **虫眼鏡**(「絞り込みの結果が 0 件」— 発注できるエリア自体は存在し、 業務エリア
 * という表示フィルタで 0 件になっている)。 分岐 (b) は**受信箱**(「そもそも無い」—
 * 広域マスタにデータが無い)。
 *
 * 導線は `<a>` (DS `TertiaryButton` の `asChild`) で描く。 画面遷移なので
 * 中クリック・新規タブを潰さない (`PlaceholderPage` と同じ扱い)。 分岐 (b) は
 * 管理者への依頼を文章のみで案内し、 導線 (ボタン) を持たない。
 *
 * ## CSV 取込への導線は置かない
 *
 * 要素は AC-BSP-105 のとおり見出し・説明・個人設定への導線だけにする。 CSV 取込は
 * 業務エリア設定に含まれないエリアの行 (`spot-bid-normal-csv-import.md`
 * AC-SPOT-008 / `spot-bid-block-csv-import.md` AC-DSB-018) と、 広域マスタに未登録の
 * エリアの行も拒否する。 このパネルが出る状態は選択できるエリアが 0 件の時なので、
 * 取込は全行が拒否され、 どちらの分岐でも迂回路にならない。
 */
export const BusinessAreaEmptyState = ({
  isBroadAreaMasterEmpty,
  issuerLabel,
  missingMasters = [],
  orderableAreaCodes,
  unknownIssuerBidCount = 0,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const withUnknownIssuerNote = (description: string): string =>
    unknownIssuerBidCount > 0
      ? `${description}\n${t('page:biddingSpot.businessAreaEmpty.unknownIssuerBids', { count: unknownIssuerBidCount })}`
      : description

  if (isBroadAreaMasterEmpty) {
    const { title, description } = masterNotRegisteredText(
      t,
      issuerLabel,
      missingMasters,
    )
    return (
      <EmptyPanel
        description={withUnknownIssuerNote(description)}
        icon={
          <FaInboxRegular boxSize={EMPTY_PANEL_ICON_SIZE} aria-hidden="true" />
        }
        title={title}
      />
    )
  }

  const areas = orderableAreaCodes.map(convertAreaLabel).join('・')

  return (
    <EmptyPanel
      action={
        <TertiaryButton asChild>
          <RouterLink to={routePaths.settings.path}>
            {t('page:biddingSpot.businessAreaEmpty.addToBusinessArea.action')}
          </RouterLink>
        </TertiaryButton>
      }
      description={withUnknownIssuerNote(
        t('page:biddingSpot.businessAreaEmpty.addToBusinessArea.description', {
          areas,
        }),
      )}
      icon={
        <FaMagnifyingGlassRegular
          boxSize={EMPTY_PANEL_ICON_SIZE}
          aria-hidden="true"
        />
      }
      title={t('page:biddingSpot.businessAreaEmpty.addToBusinessArea.title')}
    />
  )
}
